import { UploadRestricaoModel } from '@/models/ferramentas/saeweb/configuracaoSeguranca.model';
import configuracaoSegurancaService from '@/services/ferramentas/saeweb/configuracaoSeguranca.service';
import DadosLocais from '@/services/helper/storage';

const dadosLocais = DadosLocais.getInstance();

/**
 * Busca as restrições de upload configuradas na tela de Configurações de Segurança, sempre
 * direto do servidor (sem cache) — como é só um GET leve e a tela de admin pode ser editada a
 * qualquer momento na mesma sessão do SPA, cachear arriscaria validar contra uma config já
 * desatualizada.
 *
 * Retorna `null` (não `[]`) quando a busca falha ou não há contrato — distinção importante para
 * `validarArquivoUpload`: `null` significa "não sabemos as regras reais, não bloquear no
 * cliente" (o servidor decide de verdade); `[]` significa "o contrato não tem NENHUMA extensão
 * configurada", que desde a mudança de postura para negar-por-padrão passa a bloquear tudo.
 */
export async function obterRegrasUploadContratoAtual(): Promise<UploadRestricaoModel[] | null> {
  const codigoContrato = Number(dadosLocais.getCodContrato());
  if (!codigoContrato) {
    return null;
  }

  try {
    const { data } = await configuracaoSegurancaService.obterUploadRestricoes(codigoContrato);
    return data ?? [];
  } catch {
    return null;
  }
}

/**
 * Validação client-side (feedback rápido antes de enviar). Espelha extensão/tamanho/MIME do
 * UploadRestricaoService da Kernel API — a validação de conteúdo real (assinatura/magic bytes)
 * só é possível no servidor, depois que o arquivo chega.
 *
 * `regras` deve vir de `obterRegrasUploadContratoAtual`: `null` (busca falhou/sem contrato) não
 * bloqueia no cliente — o servidor é a fonte da verdade; `[]` (contrato sem nenhuma extensão
 * configurada) bloqueia tudo, espelhando a postura negar-por-padrão da Kernel API.
 */
export function validarArquivoUpload(arquivo: File, regras: UploadRestricaoModel[] | null): string | null {
  if (regras === null) {
    return null;
  }

  if (regras.length === 0) {
    return 'Nenhuma extensão de arquivo está configurada como permitida para este contrato.';
  }

  const extensao = obterExtensao(arquivo.name);
  if (!extensao) {
    return 'Arquivo sem extensão — não é possível validar o tipo anexado.';
  }

  const regra = regras.find((r) => r.ativo && normalizarExtensao(r.extensaoPermitida) === extensao);
  if (!regra) {
    return `Extensão "${extensao}" não é permitida para anexos neste contrato.`;
  }

  if (arquivo.size > regra.tamanhoMaximoBytes) {
    const maximoMb = (regra.tamanhoMaximoBytes / (1024 * 1024)).toFixed(1);
    return `Arquivo excede o tamanho máximo permitido para "${extensao}" (${maximoMb} MB).`;
  }

  if (arquivo.type && regra.mimeTypePermitido && arquivo.type.toLowerCase() !== regra.mimeTypePermitido.toLowerCase()) {
    return `Tipo de arquivo informado ("${arquivo.type}") não corresponde ao esperado para "${extensao}".`;
  }

  return null;
}

/**
 * Extensão real do arquivo (com o ponto, ex: ".png"), a partir do nome do File escolhido pelo
 * usuário. Usar isso — nunca um valor fixo tipo ".jpg" — ao nomear o arquivo enviado ao backend,
 * senão a extensão vista pelo servidor não corresponde ao conteúdo real.
 */
export function obterExtensao(nomeArquivo: string): string {
  const idx = nomeArquivo.lastIndexOf('.');
  return idx >= 0 ? nomeArquivo.substring(idx).toLowerCase() : '';
}

function normalizarExtensao(extensao: string): string {
  if (!extensao) return '';
  return `.${extensao.trim().replace(/^\./, '').toLowerCase()}`;
}

/**
 * Extrai a mensagem de erro real de uma resposta de upload rejeitada — a Academic API responde
 * com { erros: string[] } (ErrorModel) quando a Kernel API rejeita o arquivo; a Kernel API,
 * quando respondida direto, manda a mensagem como texto puro.
 */
export function extrairMensagemErroUpload(error: any, mensagemPadrao = 'Erro ao enviar arquivo.'): string {
  const dados = error?.response?.data;
  if (typeof dados === 'string') return dados;
  if (Array.isArray(dados?.erros) && dados.erros.length) return dados.erros.join(' ');
  return mensagemPadrao;
}
